/**
 * components/hire/badges.tsx
 * Shared SK Hire presentational atoms used across Requirements, Requirement
 * detail, Candidate Pool and Candidate detail — kept in one place so the owner
 * "tell of mode", status pills, score bars, skill chips and avatars never drift.
 */
import React from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Icon } from '../ui';
import { initialsOf, scoreColorKey } from '../../utils/hireFormat';
import type { OwnerType, RequirementStatus } from '../../schemas/hire/common';

/** Owner badge — the quiet "tell" of agency vs in-house mode. */
export function OwnerBadge({ ownerType, ownerLabel }: { ownerType: OwnerType; ownerLabel?: string | null }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const isClient = ownerType === 'crm_org';
  const label = isClient ? (ownerLabel || 'Client') : 'Internal';
  const fg = isClient ? colors.accentInk : colors.neutral;
  const bg = isClient ? colors.accentSoft : colors.neutralSoft;
  const line = isClient ? colors.accentLine : colors.neutralLine;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingLeft: 3, paddingRight: 11, paddingVertical: 3, borderRadius: 999, backgroundColor: bg, borderWidth: 1, borderColor: line, maxWidth: '100%' }}>
      {/* icon sits in its own light chip for an elevated, "elite" feel */}
      <View style={{ width: 18, height: 18, borderRadius: 999, backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={isClient ? 'store' : 'shield'} size={11} color={fg} />
      </View>
      <Text numberOfLines={1} style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: fg, letterSpacing: 0.2 }}>{label}</Text>
    </View>
  );
}

const STATUS_META: Record<RequirementStatus, { label: string; key: 'info' | 'warn' | 'success' | 'neutral' }> = {
  open: { label: 'Open', key: 'info' },
  on_hold: { label: 'On hold', key: 'warn' },
  filled: { label: 'Filled', key: 'success' },
  closed: { label: 'Closed', key: 'neutral' },
};

export function StatusPill({ status }: { status: RequirementStatus }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const meta = STATUS_META[status] ?? STATUS_META.open;
  const fg = colors[meta.key];
  const bg = colors[`${meta.key}Soft` as keyof typeof colors] as string;
  const line = colors[`${meta.key}Line` as keyof typeof colors] as string;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: bg, borderWidth: 1, borderColor: line }}>
      <View style={{ width: 6, height: 6, borderRadius: 999, backgroundColor: fg }} />
      <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: fg }}>{meta.label}</Text>
    </View>
  );
}

/** Round initials avatar (paper-3 fill). */
export function MiniAvatar({ name, size = 26 }: { name?: string | null; size?: number }) {
  const { theme } = useTheme();
  const { colors, fontWeight } = theme;
  return (
    <View style={{ width: size, height: size, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper3 }}>
      <Text style={{ fontSize: size * 0.38, fontWeight: fontWeight.bold, color: colors.ink2 }}>{initialsOf(name)}</Text>
    </View>
  );
}

/** Thin score progress bar coloured by the match-score thresholds. */
export function ScoreBar({ pct }: { pct: number }) {
  const { theme } = useTheme();
  const { colors } = theme;
  const fg = colors[scoreColorKey(pct)];
  return (
    <View style={{ height: 4, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden' }}>
      <View style={{ height: 4, borderRadius: 999, width: `${Math.max(0, Math.min(100, pct))}%`, backgroundColor: fg }} />
    </View>
  );
}

/** Row of skill chips. tone='accent' (matched-skill emphasis) or 'neutral' (plain grey). */
export function SkillChips({ skills, max = 4, tone = 'accent' }: { skills?: string[] | null; max?: number; tone?: 'accent' | 'neutral' }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  // Defensive: a caller may pass undefined/null (e.g. a match with no
  // matchingSkills). Never crash the whole screen over an empty chip row.
  const list = Array.isArray(skills) ? skills : [];
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const bg = tone === 'accent' ? colors.accentSoft : colors.paper2;
  const line = tone === 'accent' ? colors.accentLine : colors.line;
  const fg = tone === 'accent' ? colors.accentInk : colors.ink2;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
      {shown.map((s, i) => (
        <View key={`${s}-${i}`} style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: bg, borderWidth: 1, borderColor: line }}>
          <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: fg }}>{s}</Text>
        </View>
      ))}
      {list.length > max && (
        <Text style={{ fontSize: fontSize.xs, color: colors.ink4, alignSelf: 'center' }}>+{list.length - max}</Text>
      )}
    </View>
  );
}
